import { DEFAULT_CONFIG } from '@planuze/config';
import { z } from 'zod';
import { createPlanShape } from './plans.js';

/* Namespace `@slug` do publisher da org (ADR-0600): fonte única do pattern =
   `@planuze/config` (§4.17). Regra do PUBLISHER (alfanumérico nas pontas), distinta
   do slug da org. `max(39)` alinha ao teto do pattern (1 + 37 + 1). */
const PUBLISHER_SLUG_PATTERN = new RegExp(DEFAULT_CONFIG.publisher.slugPattern);

/* Contratos de gestão de companies do backoffice (Epic 28 / ADR-0347).
   `status` espelha `organizations.status` do auth-service (active|trial|blocked).
   O admin-service agrega org (auth) + subscription (billing) + auditoria local. */

export const companyStatusSchema = z.enum(['active', 'trial', 'blocked']);
export type CompanyStatus = z.infer<typeof companyStatusSchema>;

/* MVP: ordena por `createdAt desc` (mais recentes primeiro) com keyset cursor
   `(createdAt,id)`. Sort multi-coluna (name/mrr) é follow-up — admin filtra/busca. */
export const listCompaniesQuery = z.object({
  status: companyStatusSchema.optional(),
  search: z.string().min(2).max(100).optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListCompaniesQuery = z.infer<typeof listCompaniesQuery>;

export const companyListItemDto = z.object({
  id: z.string(),
  name: z.string(),
  status: companyStatusSchema,
  memberCount: z.number().int(),
  createdAt: z.string(),
});
export type CompanyListItem = z.infer<typeof companyListItemDto>;

export const companyMemberDto = z.object({
  userId: z.string(),
  email: z.string(),
  role: z.string(),
  joinedAt: z.string(),
});

/* Assinatura da org agregada do billing-service (ADR-0347). Além de status/assentos,
   projeta o que já vive na row do Stripe/DB: plano cru (lookup_key), preço+moeda,
   janela do período, cancelamento e mudança de plano agendados. Todos nullable pois a
   row pode não ter o campo (sub sem preço reconciliado, sem agendamento etc.). */
export const companySubscriptionDto = z.object({
  status: z.string(),
  quantity: z.number().int(),
  /* Nome/tier cru do plano = Stripe lookup_key da row (`subscriptions.plan`). */
  plan: z.string().nullable(),
  /* Nome/tier amigáveis resolvidos do plan_catalog pelo billing: `planDisplayName` =
     displayName ?? lookup_key; `planTier` = tier (null se o lookup_key não tiver row). */
  planDisplayName: z.string().nullable(),
  planTier: z.string().nullable(),
  /* Preço unitário (cents) + moeda (ISO minúsculo, ex. `brl`) do item; `null` em rows
     ainda não reconciliadas de um evento Stripe. */
  priceCents: z.number().int().nullable(),
  currency: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  /* Cancelamento agendado (`cancel_at`) — encerra ao fim do período quando presente. */
  cancelAt: z.string().nullable(),
  /* Downgrade/upgrade agendado (Stripe Subscription Schedule): troca p/ `pendingPlan`
     em `pendingPlanAt`. */
  pendingPlan: z.string().nullable(),
  pendingPlanAt: z.string().nullable(),
});

export const companyAuditEntryDto = z.object({
  action: z.string(),
  actorId: z.string(),
  /* Email do operador via LEFT JOIN em `staff_users` (ADR-0708); `null` p/ ator órfão. */
  actorEmail: z.string().nullable(),
  ts: z.string(),
});

export const companyDetailDto = companyListItemDto.extend({
  /* Campos de conta da org projetados do auth-service (ADR-0600/0615): namespace `@slug`
     do publisher, razão social (`legalName`)/CNPJ (`taxId`), logo. `slug`/`updatedAt` são
     notNull na row → sempre presentes; o resto nullable. */
  slug: z.string(),
  legalName: z.string().nullable(),
  taxId: z.string().nullable(),
  publisherSlug: z.string().nullable(),
  logoUrl: z.string().nullable(),
  updatedAt: z.string(),
  blockedAt: z.string().nullable(),
  blockedReason: z.string().nullable(),
  /* Graça de exclusão pedida (`deletion_requested_at`): a org está em janela de exclusão
     mas ainda restaurável → a UI mostra aviso. `null` = sem pedido. */
  deletionRequestedAt: z.string().nullable(),
  members: z.array(companyMemberDto),
  subscription: companySubscriptionDto.nullable(),
  recentAudit: z.array(companyAuditEntryDto),
});
export type CompanyDetail = z.infer<typeof companyDetailDto>;

/* Criação de org pelo backoffice (admin-created — ADR-0421): orgs deixam de ser
   self-serve. `ownerUserId` = conta EXISTENTE designada como owner inicial
   (resolvida no admin-web via a busca de usuários). A feature de domínio da org
   (auto-join/gating/DNS) foi REMOVIDA (ADR-0631) — o campo `domain` não existe mais
   no create; membros entram só por convite (aberto). */
export const createCompanyBody = z.object({
  name: z.string().min(2).max(120),
  ownerUserId: z.string().min(1),
  /* Namespace `@org` do publisher da org (ADR-0600), cadastrado pelo STAFF — único
     global (user|org não colidem), imutável pós-provision (ADR-0439). Colisão é
     pré-checada no admin-web e é fail-closed no provision (409 slug_taken). */
  publisherSlug: z.string().min(1).max(39).regex(PUBLISHER_SLUG_PATTERN),
  /* Logo da org (ADR-0615): data URL `data:image/png|jpeg|webp;base64,…` opcional,
     enviada pelo staff no create-company. O MIME/tamanho são validados NO
     auth-service (§4.15 — o admin-web só dá preview); aqui só o teto de chars do
     payload (base64 infla ~33%; alinhado ao schema de avatar do auth). */
  logoDataUrl: z.string().min(1).max(1_500_000).optional(),
});
export type CreateCompanyBody = z.infer<typeof createCompanyBody>;

/* Lookup de disponibilidade global do namespace (ADR-0600): base do pré-check
   debounced do create-company. O provision no licensing é a autoridade final. */
export const publisherSlugAvailableQuery = z.object({ slug: z.string().min(1) });
export type PublisherSlugAvailableQuery = z.infer<typeof publisherSlugAvailableQuery>;
export const publisherSlugAvailableDto = z.object({ available: z.boolean() });
export type PublisherSlugAvailableDto = z.infer<typeof publisherSlugAvailableDto>;

/* Atribuir o plano CUSTOM da org (C6 — ADR-0422): o admin DEFINE o plano da org
   (tier + caps próprias + Stripe Price); o owner CONCLUI a assinatura (paga, C3). É a
   definição de plano do B3 sem o alvo no body — a URL (`:id`) é a fonte do orgId. Omite os
   campos de alvo/validade (ADR-0435), que são exclusivos da aba Negociados. */
export const assignCompanyPlanBody = createPlanShape.omit({
  orgId: true,
  userId: true,
  validFrom: true,
  validUntil: true,
  reason: true,
  /* Plano de org é DIRECIONADO (nunca no catálogo público) → a flag de visibilidade
     (ADR-0508) não se aplica; o billing usa o default. */
  isPublic: true,
  /* Herança (ADR-0595) é do form de plano negociado; a atribuição direta a company não a usa. */
  inheritedPlanKeys: true,
});
export type AssignCompanyPlanBody = z.infer<typeof assignCompanyPlanBody>;

/* `reason` ≥20 chars: força justificativa real numa ação destrutiva (ADR-0347). */
export const blockCompanyBody = z.object({ reason: z.string().min(20).max(500) });
export type BlockCompanyBody = z.infer<typeof blockCompanyBody>;

export const unblockCompanyBody = z.object({ reason: z.string().min(20).max(500) });
export type UnblockCompanyBody = z.infer<typeof unblockCompanyBody>;

/**
 * Governança em lote reutiliza exatamente o cascade da ação individual. O teto é menor que
 * listas locais porque cada empresa pode acionar Auth, Licensing e Billing.
 */
export const COMPANY_BULK_MAX_IDS = 10;
export const companyBulkActionSchema = z.enum(['block', 'unblock']);
export type CompanyBulkAction = z.infer<typeof companyBulkActionSchema>;
export const bulkCompanyActionBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(COMPANY_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: companyBulkActionSchema,
  reason: z.string().min(20).max(500),
});
export type BulkCompanyActionBody = z.infer<typeof bulkCompanyActionBody>;

/* Outcome do cascade de block (ADR-0347): `partial_success` quando um step
   best-effort (sessions/billing) falha mas o soft-block (gate) aplicou. */
export const blockOutcomeDto = z.object({
  status: z.enum(['success', 'partial_success', 'already_blocked']),
  failedSteps: z.array(z.string()),
});
export type BlockOutcome = z.infer<typeof blockOutcomeDto>;

export const bulkCompanyActionResponseDto = z.object({
  results: z.array(
    z.discriminatedUnion('ok', [
      z.object({ id: z.string(), ok: z.literal(true), error: z.null(), outcome: blockOutcomeDto }),
      z.object({ id: z.string(), ok: z.literal(false), error: z.string(), outcome: z.null() }),
    ]),
  ),
});
export type BulkCompanyActionResponse = z.infer<typeof bulkCompanyActionResponseDto>;
